import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import config from '@/config';
import useGoBack from '@/hooks/useGoBack';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from '@/services/fetchWrapper.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import RankingIcon from '@/shared-components/icons/components/navigation/RankingIcon';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import {
  getColorDistance,
  gradientBetween,
  MAX_RGB_DISTANCE,
  mixComponentsToRgb,
  rgbToCss,
  rgbToHsl,
} from '@/utils/gameMixColor.utils';
import { getSsrArtGamesBootstrap } from '@/utils/ssrArtGamesBootstrap';

import ArtGameHeader from '../components/ArtGameHeader';

import {
  createRoundPayload,
  evaluateRound,
  fetchLeaderboard,
  getStreakLabel,
  submitRoundResult,
  useColorMixLeaderboard,
} from './leaderboard';
import LeaderboardPanel from './LeaderboardPanel';
import SharePopup from './SharePopup';

import './index.css';

const BASE_COLORS = {
  red: { labelKey: 'colors.red', letter: 'R', rgb: { r: 255, g: 0, b: 0 }, accent: '#ff3b30' },
  yellow: { labelKey: 'colors.yellow', letter: 'Y', rgb: { r: 255, g: 255, b: 0 }, accent: '#ffd60a' },
  blue: { labelKey: 'colors.blue', letter: 'B', rgb: { r: 0, g: 0, b: 255 }, accent: '#3a7bff' },
  white: { labelKey: 'colors.white', letter: 'W', rgb: { r: 255, g: 255, b: 255 }, accent: '#f4f5f8' },
  black: { labelKey: 'colors.black', letter: 'K', rgb: { r: 0, g: 0, b: 0 }, accent: '#1b1f25' },
};

const COLOR_KEYS = Object.keys(BASE_COLORS);

const EMPTY_COLOR = { r: 236, g: 236, b: 240 };

const POUR_LIMIT = 24;
const TAP_AMOUNT = 1;
const HOLD_INCREMENT = 0.35;
const HOLD_INTERVAL = 110;
const HOLD_DELAY = 210;

const STAGE_FLOW = [
  { key: 'core', label: 'Core', rounds: 6, description: 'Dial in trickier midpoints and balances.' },
  { key: 'advanced', label: 'Advanced', rounds: Infinity, description: 'Low-saturation tints and shades test your intuition.' },
];

const createEmptyMix = () => COLOR_KEYS.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});

const randomInRange = (min, max) => Math.random() * (max - min) + min;

const PRIMARY_KEYS = ['red', 'yellow', 'blue'];

const createCoreTargetMix = () => {
  const components = createEmptyMix();
  let totalPrimaries = 0;

  PRIMARY_KEYS.forEach((key) => {
    const amount = randomInRange(0, 1.2);
    components[key] = amount;
    totalPrimaries += amount;
  });

  if (totalPrimaries < 0.6) {
    const boostKey = PRIMARY_KEYS[Math.floor(Math.random() * PRIMARY_KEYS.length)];
    components[boostKey] += randomInRange(0.6, 1.1);
  }

  if (Math.random() < 0.7) {
    components.white = randomInRange(0.05, 0.75);
  }

  if (Math.random() < 0.6) {
    components.black = randomInRange(0.05, 0.6);
  }

  return components;
};

const createAdvancedTargetMix = () => {
  const components = createEmptyMix();

  PRIMARY_KEYS.forEach((key) => {
    components[key] = randomInRange(0, 0.75);
  });

  if (Math.random() < 0.5) {
    const focusKey = PRIMARY_KEYS[Math.floor(Math.random() * PRIMARY_KEYS.length)];
    components[focusKey] += randomInRange(0.3, 0.9);
  }

  components.white = randomInRange(0.45, 1.35);

  if (Math.random() < 0.85) {
    components.black = randomInRange(0.25, 1);
  }

  return components;
};

const TARGET_MIX_GENERATORS = {
  core: createCoreTargetMix,
  advanced: createAdvancedTargetMix,
};

const TARGET_STAGE_CONSTRAINTS = {
  core: ({ saturation, lightness }) => saturation >= 0.32 && saturation <= 0.82 && lightness >= 0.3 && lightness <= 0.72,
  advanced: ({ saturation, lightness }) => saturation >= 0.08 && saturation <= 0.48 && lightness >= 0.25 && lightness <= 0.8,
};

const generateTargetColor = (stageKey) => {
  const generator = TARGET_MIX_GENERATORS[stageKey] ?? TARGET_MIX_GENERATORS.core;
  const constraint = TARGET_STAGE_CONSTRAINTS[stageKey] ?? TARGET_STAGE_CONSTRAINTS.core;
  const maxAttempts = 40;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const components = generator();
    const candidate = mixComponentsToRgb(components, BASE_COLORS, EMPTY_COLOR);
    const [, saturation, lightness] = rgbToHsl(candidate);

    if (constraint({ saturation, lightness })) {
      return candidate;
    }
  }

  const fallbackMix = generator();
  return mixComponentsToRgb(fallbackMix, BASE_COLORS, EMPTY_COLOR);
};

const determineStageKey = (round) => {
  let roundsConsumed = 0;

  for (const stage of STAGE_FLOW) {
    if (!Number.isFinite(stage.rounds)) {
      return stage.key;
    }

    roundsConsumed += stage.rounds;
    if (round <= roundsConsumed) {
      return stage.key;
    }
  }

  return STAGE_FLOW[STAGE_FLOW.length - 1].key;
};

const gradeKeyFromPercentage = (percentage) => {
  if (percentage >= 97) {
    return 'flawless_match';
  }

  if (percentage >= 93) {
    return 'gallery_ready';
  }

  if (percentage >= 88) {
    return 'great_eye';
  }

  if (percentage >= 75) {
    return 'getting_close';
  }

  return 'keep_exploring';
};

const formatPourValue = (value) => {
  if (value % 1 === 0) {
    return value.toString();
  }

  return value.toFixed(1);
};

function readInitialColorMixSeoPayload() {
  const payload = getSsrArtGamesBootstrap('color-mix');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

export default function ColorMixGame() {
  const t = useTranslations('common.newspaper.art_games.color_mix');
  const leaderboardT = useTranslations('common.newspaper.art_games.color_mix.leaderboard');
  const goBackHandler = useGoBack('/1/newspaper/art-games');
  const { state } = useStateHandler();
  const sessionUsername = state?.user_session?.user_username;
  const [round, setRound] = useState(1);
  const [stageKey, setStageKey] = useState(() => determineStageKey(1));
  const [targetColor, setTargetColor] = useState(() => generateTargetColor(stageKey));
  const [, setMixComponents] = useState(() => createEmptyMix());
  const [mixColor, setMixColor] = useState(EMPTY_COLOR);
  const [poursUsed, setPoursUsed] = useState(0);
  const poursRef = useRef(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [totalPours, setTotalPours] = useState(0);
  const [resultCard, setResultCard] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);
  const [isSavingRound, setIsSavingRound] = useState(false);
  const holdRefs = useRef({});
  const [activeButton, setActiveButton] = useState(null);
  const [bestScore, setBestScore] = useState(0);
  // best % inside the current run (resets on miss)
  const [sessionBestScore, setSessionBestScore] = useState(0);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(null);
  const leaderboardData = useColorMixLeaderboard();
  const [isLeaderboardPopupOpen, setLeaderboardPopupOpen] = useState(false);
  const [colorMixSeo, setColorMixSeo] = useState(() => readInitialColorMixSeoPayload());

  useEffect(() => {
    let ignore = false;

    async function loadColorMixSeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/art-games/color-mix`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setColorMixSeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load Color Mix SEO payload', error);
        }
      }
    }

    loadColorMixSeo();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadPersistedStats = async () => {
      try {
        const leaderboard = await fetchLeaderboard();
        if (!isMounted) {
          return;
        }

        const userStats = leaderboard?.user;
        if (!userStats) {
          return;
        }

        if (Number.isFinite(userStats.bestStreak)) {
          setBestStreak((previousBest) => Math.max(previousBest, userStats.bestStreak));
        } else if (Number.isFinite(userStats.streak)) {
          setBestStreak((previousBest) => Math.max(previousBest, userStats.streak));
        }
      } catch (_error) {
        // Silently ignore leaderboard load failures; the UI will fall back to local state.
      }
    };

    loadPersistedStats();

    return () => {
      isMounted = false;
    };
  }, [fetchLeaderboard]);

  const streakLabel = useMemo(() => getStreakLabel(streak), [streak]);

  const addColor = useCallback((colorKey, amount) => {
    setMixComponents((previous) => {
      if (poursRef.current >= POUR_LIMIT) {
        return previous;
      }

      const effectiveAmount = Math.min(amount, POUR_LIMIT - poursRef.current);
      if (effectiveAmount <= 0) {
        return previous;
      }

      const updated = {
        ...previous,
        [colorKey]: (previous[colorKey] ?? 0) + effectiveAmount,
      };

      const nextMixColor = mixComponentsToRgb(updated, BASE_COLORS, EMPTY_COLOR);

      poursRef.current = Number((poursRef.current + effectiveAmount).toFixed(2));
      setPoursUsed(poursRef.current);
      setMixColor(nextMixColor);

      return updated;
    });
  }, []);

  const resetMix = useCallback(() => {
    setMixComponents(createEmptyMix());
    setMixColor(EMPTY_COLOR);
    poursRef.current = 0;
    setPoursUsed(0);
  }, []);

  // Reset the entire "run" after a miss: round+streak+points+pours restart
  const resetRun = useCallback(() => {
    setStreak(0);
    setRound(1);
    const firstStage = determineStageKey(1);
    setStageKey(firstStage);
    setTargetColor(generateTargetColor(firstStage));
    setTotalPoints(0);
    setTotalPours(0);
    setSessionBestScore(0);
    resetMix();
  }, [resetMix]);

  const handleAcceptMix = useCallback(() => {
    if (poursRef.current <= 0) return;

    const poursForRound = poursRef.current;
    const closenessDistance = getColorDistance(mixColor, targetColor);
    const closeness = Math.max(0, 100 * (1 - closenessDistance / MAX_RGB_DISTANCE));
    const gradeKey = gradeKeyFromPercentage(closeness);

    const evaluation = evaluateRound({
      match: closeness,
      poursUsed: poursForRound,
      streak,
      totalPoints,
      totalPours,
    });

    const nextResult = {
      closeness: evaluation.clampedMatch,
      gradeKey,
      mix: mixColor,
      target: targetColor,
      highlight: evaluation.clampedMatch >= 95,
      pours: poursForRound,
      threshold: evaluation.threshold,
      roundPoints: evaluation.roundPoints,
      isCorrect: evaluation.isCorrect,
    };

    // Track best % inside the current session only
    if (Number.isFinite(evaluation.clampedMatch)) {
      const roundedMatch = Number(evaluation.clampedMatch.toFixed(1));
      if (Number.isFinite(roundedMatch)) {
        setSessionBestScore((prev) => Math.max(prev, roundedMatch));
      }
    }

    setResultCard(nextResult);
    setSubmissionError(null);

    if (evaluation.isCorrect) {
      const nextStreak = evaluation.nextStreak;
      setStreak(nextStreak);

      // Advance round only on correct
      setRound((prevRound) => {
        const nr = prevRound + 1;
        const nextStage = determineStageKey(nr);
        setStageKey(nextStage);
        setTargetColor(generateTargetColor(nextStage));
        return nr;
      });

      // Upgrade bestStreak; if this creates a new best streak, promote sessionBestScore to global bestScore
      setBestStreak((prevBest) => {
        const isNewBest = nextStreak > prevBest;
        if (isNewBest) {
          setBestScore((prev) => Math.max(prev, sessionBestScore));
        }
        return Math.max(prevBest, nextStreak);
      });

      // Keep accumulating inside the run
      setTotalPoints(evaluation.updatedTotalPoints);
      setTotalPours(evaluation.updatedTotalPours);

      // fresh bin for next round
      resetMix();
    } else {
      // On miss: if this run ties your best streak, allow bestScore to improve
      if (streak === bestStreak) {
        setBestScore((prev) => Math.max(prev, sessionBestScore));
      }
      // Full run reset: round, streak, points, pours, sessionBestScore
      resetRun();
    }

    // Save round (unchanged)
    const payload = createRoundPayload({
      match: evaluation.clampedMatch,
      poursUsed: poursForRound,
      roundPoints: evaluation.roundPoints,
      userUsername: sessionUsername,
      roundId:
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `round-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    });

    if (payload) {
      setIsSavingRound(true);
      submitRoundResult(payload, { emitUpdateEvent: !evaluation.isCorrect })
        .then((submission) => {
          const stats = submission?.data?.stats ?? submission?.stats;
          if (!stats) return;
          if (Number.isFinite(stats.bestStreak)) {
            setBestStreak(stats.bestStreak);
          } else if (Number.isFinite(stats.streak)) {
            setBestStreak((previousBest) => Math.max(previousBest, stats.streak));
          }
        })
        .catch((error) => {
          setSubmissionError(error);
        })
        .finally(() => {
          setIsSavingRound(false);
        });
    }
  }, [
    mixColor,
    resetMix,
    resetRun,
    streak,
    bestStreak,
    targetColor,
    totalPoints,
    totalPours,
    sessionBestScore,
    sessionUsername,
  ]);

  useEffect(() => () => {
    Object.values(holdRefs.current).forEach((data) => {
      if (data) {
        clearTimeout(data.timeoutId);
        clearInterval(data.intervalId);
      }
    });
  }, []);

  const stopHold = useCallback((colorKey, cancelTap = false) => {
    const holdData = holdRefs.current[colorKey];
    if (!holdData) {
      setActiveButton((previous) => (previous === colorKey ? null : previous));
      return;
    }

    clearTimeout(holdData.timeoutId);
    clearInterval(holdData.intervalId);

    if (!holdData.isHolding && !cancelTap) {
      addColor(colorKey, TAP_AMOUNT);
    }

    holdRefs.current[colorKey] = undefined;
    setActiveButton((previous) => (previous === colorKey ? null : previous));
  }, [addColor]);

  const handlePointerDown = useCallback((colorKey) => (event) => {
    event.preventDefault();
    if (poursRef.current >= POUR_LIMIT) {
      return;
    }

    setActiveButton(colorKey);

    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(12);
    }

    const timeoutId = setTimeout(() => {
      const intervalId = setInterval(() => {
        addColor(colorKey, HOLD_INCREMENT);
      }, HOLD_INTERVAL);

      holdRefs.current[colorKey] = {
        ...holdRefs.current[colorKey],
        isHolding: true,
        timeoutId,
        intervalId,
      };
    }, HOLD_DELAY);

    holdRefs.current[colorKey] = {
      isHolding: false,
      timeoutId,
      intervalId: undefined,
    };
  }, [addColor]);

  const handlePointerUp = useCallback((colorKey) => (event) => {
    event.preventDefault();
    stopHold(colorKey);
  }, [stopHold]);

  const handlePointerLeave = useCallback((colorKey) => () => {
    stopHold(colorKey, true);
  }, [stopHold]);

  const handleKeyDown = useCallback((colorKey) => (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      addColor(colorKey, TAP_AMOUNT);
    }
  }, [addColor]);

  const openSharePopup = useCallback(() => {
    setShareFeedback(null);
    setIsShareOpen(true);
  }, []);

  const closeSharePopup = useCallback(() => {
    setIsShareOpen(false);
    setShareFeedback(null);
    setIsSharing(false);
  }, []);

  const closeLeaderboardPopup = useCallback(() => {
    setLeaderboardPopupOpen(false);
  }, []);

  const openLeaderboardPopup = useCallback(() => {
    setLeaderboardPopupOpen(true);
  }, []);

  const handleShareInvite = useCallback(async () => {
    if (isSharing) {
      return;
    }

    const shareUrl = typeof window !== 'undefined'
      ? `${window.location.origin}/newspaper/art-games/color-mix`
      : 'https://suarte.art/newspaper/art-games/color-mix';

    const hasScore = bestScore > 0;
    const formattedScore = hasScore ? `${bestScore.toFixed(1)}%` : null;
    const shareText = hasScore
      ? t('share.score_text', { score: formattedScore })
      : t('share.default_text');

    const payload = {
      title: t('share.title'),
      text: shareText,
      url: shareUrl,
    };

    setIsSharing(true);
    setShareFeedback(null);

    try {
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        await navigator.share(payload);
        setShareFeedback({ tone: 'success', message: t('share.feedback.invite_sent') });
      } else if (
        typeof navigator !== 'undefined'
        && navigator.clipboard
        && typeof navigator.clipboard.writeText === 'function'
      ) {
        await navigator.clipboard.writeText(`${payload.text} ${payload.url}`);
        setShareFeedback({ tone: 'success', message: t('share.feedback.link_copied') });
      } else {
        setShareFeedback({ tone: 'info', message: t('share.feedback.manual_share'), link: shareUrl });
      }
    } catch (_shareError) {
      if (
        typeof navigator !== 'undefined'
        && navigator.clipboard
        && typeof navigator.clipboard.writeText === 'function'
      ) {
        try {
          await navigator.clipboard.writeText(`${payload.text} ${payload.url}`);
          setShareFeedback({ tone: 'success', message: t('share.feedback.copied_fallback') });
        } catch (_clipboardError) {
          setShareFeedback({ tone: 'error', message: t('share.feedback.auto_share_failed'), link: shareUrl });
        }
      } else {
        setShareFeedback({ tone: 'error', message: t('share.feedback.open_share_failed'), link: shareUrl });
      }
    } finally {
      setIsSharing(false);
    }
  }, [bestScore, isSharing, t]);

  const poursProgress = Math.min(1, poursUsed / POUR_LIMIT);

  const poursDescription = `${formatPourValue(poursUsed)} / ${POUR_LIMIT}`;

  return (
    <>
      <StaticSeoHead
        title={colorMixSeo?.title || t('meta.title')}
        description={colorMixSeo?.metaDescription || t('meta.description')}
        canonicalUrl={colorMixSeo?.canonicalUrl}
        canonicalPath={colorMixSeo?.canonicalPath || '/newspaper/art-games/color-mix'}
        robots={colorMixSeo?.robots}
        ogType={colorMixSeo?.ogType}
        image={colorMixSeo?.ogImage}
        pageType={colorMixSeo?.pageType || 'WebApplication'}
        structuredData={colorMixSeo?.structuredData}
      />
      <div className="color-mix-page">
        <ArtGameHeader
          className="color-mix__header"
          titleClassName="color-mix__title"
          title={t('hero.title')}
          onBack={goBackHandler}
          rightSlot={
            <button
              type="button"
              className="color-mix__leaderboard-button"
              onClick={openLeaderboardPopup}
              aria-label={leaderboardT('title')}
            >
              <span className="color-mix__leaderboard-button-label">{leaderboardT('title')}</span>
              <RankingIcon className="color-mix__leaderboard-button-icon" />
            </button>
          }
          onShare={openSharePopup}
          shareAriaLabel={t('hero.share_aria')}
        />

        <div className="color-mix__workspace">
          <div className="color-mix__sidebar">
            {resultCard && (
              <aside className={`color-mix__result ${resultCard.highlight ? 'color-mix__result--celebrate' : ''}`}>
                <button
                  type="button"
                  className="color-mix__result-close"
                  onClick={() => setResultCard(null)}
                  aria-label={t('result.close_aria')}
                >
                  <XIcon />
                </button>
                <h2 className="color-mix__result-title">{t(`result.grades.${resultCard.gradeKey}`)}</h2>
                <p className="color-mix__result-score">{t('result.match', { score: resultCard.closeness.toFixed(1) })}</p>
                {typeof resultCard.threshold === 'number' && (
                  <p className="color-mix__result-threshold">
                    {t('result.threshold', { threshold: resultCard.threshold, points: resultCard.roundPoints })}
                  </p>
                )}
                <div
                  className="color-mix__difference"
                  style={{ background: gradientBetween(resultCard.mix, resultCard.target) }}
                >
                  <span>{t('result.mix_label')}</span>
                  <span>{t('result.target_label')}</span>
                </div>
                <span className="color-mix__result-footnote">{t('result.pours_used', { value: formatPourValue(resultCard.pours) })}</span>
              </aside>
            )}
          </div>
          <main className="color-mix__content">
            <section className="color-mix__target" aria-label={t('aria.target_swatch')}>
              <span className="sr-only">{t('aria.match_this_color')}</span>
              <div
                className="color-mix__swatch color-mix__target-swatch"
                style={{ background: rgbToCss(targetColor) }}
              />
            </section>

            <section className="color-mix__status">
              <div className="color-mix__stat">
                <span className="color-mix__stat-label">{t('stats.round')}</span>
                <span className="color-mix__stat-value">{round}</span>
              </div>
              <div className="color-mix__stat">
                <span className="color-mix__stat-label">{t('stats.points')}</span>
                <span className="color-mix__stat-value">{totalPoints}</span>
              </div>
              <div className="color-mix__stat">
                <span className="color-mix__stat-label">{t('stats.best')}</span>
                <span className="color-mix__stat-value">{bestStreak}</span>
              </div>
              <div className="color-mix__stat">
                <span className="color-mix__stat-label">{t('stats.pours')}</span>
                <span className="color-mix__stat-value">{poursDescription}</span>
                <div className="color-mix__progress">
                  <div className="color-mix__progress-bar" style={{ width: `${poursProgress * 100}%` }} />
                </div>
              </div>
              <div className="color-mix__stat">
                <span className="color-mix__stat-label">{t(`streak_labels.${streakLabel}`)}</span>
                <span className="color-mix__stat-value">{streak}</span>
              </div>
              <div className="color-mix__stat">
                <span className="color-mix__stat-label">{t('stats.total_pours')}</span>
                <span className="color-mix__stat-value">{totalPours}</span>
              </div>
            </section>

            <section className="color-mix__bin" aria-labelledby="mix-bin-label">
              <div
                className="color-mix__swatch color-mix__mix-swatch"
                style={{ background: rgbToCss(mixColor) }}
              />
            </section>

            <section className="color-mix__controls" aria-label={t('aria.pour_colors')}>
              <div className="color-mix__buttons">
                {COLOR_KEYS.map((colorKey) => {
                  const baseColor = BASE_COLORS[colorKey];
                  const isDisabled = poursRef.current >= POUR_LIMIT;
                  return (
                    <div className="color-mix__button-wrap" key={colorKey}>
                      <button
                        type="button"
                        className={`color-mix__button ${colorKey} ${activeButton === colorKey ? 'is-active' : ''}`}
                        aria-label={t('controls.pour_color', { color: t(baseColor.labelKey) })}
                        onPointerDown={handlePointerDown(colorKey)}
                        onPointerUp={handlePointerUp(colorKey)}
                        onPointerLeave={handlePointerLeave(colorKey)}
                        onPointerCancel={handlePointerLeave(colorKey)}
                        onKeyDown={handleKeyDown(colorKey)}
                        disabled={isDisabled}
                        style={{
                          '--color-accent': baseColor.accent,
                          '--color-accent-light': baseColor.accentLight,
                          '--color-accent-dark': baseColor.accentDark,
                        }}
                      >
                        <span className="color-mix__button-outer">
                          <span className="color-mix__button-shadow" />
                          <span className="color-mix__button-inner" />
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="color-mix__actions">
                <ContinueButton type="button" className="color-mix__secondary" onClick={resetMix}>
                  Reset
                </ContinueButton>
                <ContinueButton
                  type="button"
                  className="color-mix__primary"
                  onClick={handleAcceptMix}
                  disabled={poursUsed <= 0 || isSavingRound}
                >
                  {isSavingRound ? t('controls.saving') : 'Accept'}
                </ContinueButton>
              </div>
              {submissionError ? (
                <p role="alert" className="color-mix__feedback color-mix__feedback--error">
                  {t('errors.round_save_failed', { reason: submissionError.message })}
                </p>
              ) : null}
            </section>
          </main>
        </div>
      </div>
      <SharePopup
        open={isShareOpen}
        onClose={closeSharePopup}
        highestScore={bestScore}
        onShare={handleShareInvite}
        shareFeedback={shareFeedback}
        isSharing={isSharing}
      />
      <GenericPopup
        open={isLeaderboardPopupOpen}
        onCloseAction={closeLeaderboardPopup}
        className="color-mix__leaderboard-popup"
        opacity
      >
        <LeaderboardPanel {...leaderboardData} onClose={closeLeaderboardPopup} />
      </GenericPopup>
    </>
  );
}
